import {
  Badge,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { useQuery } from '@tanstack/react-query';
import { ListChecksIcon } from 'lucide-react';
import { approvalChainsQuery } from '@/entities/approval-chain';
import { summarizeFolderChains } from '@/entities/asset-approval-chain';
import {
  projectFolderFilesQuery,
  type FolderApprovalProgressSummary,
} from '@/entities/project-folder';
import { useTranslation } from 'react-i18next';

interface FolderApprovalChainTagsProps {
  projectId: string;
  folderId: string;
  folderProgress: FolderApprovalProgressSummary;
  deriveChains: boolean;
  /** The chain new uploads into the folder get; named on its own while the folder is empty. */
  newUploadsApprovalChainId?: string | null;
}

interface SummaryTag {
  key: string;
  /**
   * The chain this counts. Absent for assets no chain tracks, which are
   * reported as a bare count: naming that group invents a chain the folder
   * does not have.
   */
  name?: string;
  approved: number;
  total: number;
}

export function FolderApprovalChainTags(props: FolderApprovalChainTagsProps) {
  const { t } = useTranslation('projects');

  const { projectId, folderId, folderProgress, deriveChains } = props;

  const { data } = useQuery({
    ...projectFolderFilesQuery(projectId, folderId, {
      sortBy: 'latest_version_created_at',
      sortOrder: 'DESC',
    }),
    enabled: deriveChains,
  });

  const files = deriveChains ? (data?.items ?? []) : [];
  const { chains, defaults } = summarizeFolderChains(files);

  const standardBucket =
    chains.length === 0
      ? { approved: folderProgress.approvedCount, total: folderProgress.totalCount }
      : { approved: defaults.approved, total: defaults.total };

  const tags: SummaryTag[] = [
    ...chains.map((chain) => ({
      key: `chain:${chain.name}`,
      name: chain.name,
      approved: chain.completed,
      total: chain.total,
    })),
    ...(standardBucket.total > 0 ? [{ key: 'standard', ...standardBucket }] : []),
  ];

  const { data: approvalChains } = useQuery({
    ...approvalChainsQuery(projectId),
    enabled: !!props.newUploadsApprovalChainId,
  });
  // A deleted chain is missing from the list, and no longer applies, so it names nothing.
  const newUploadsApprovalChain = approvalChains?.items.find(
    (approvalChain) => approvalChain.id === props.newUploadsApprovalChainId,
  );

  if (tags.length === 0 && !newUploadsApprovalChain) return null;

  const newUploadsLabel = newUploadsApprovalChain
    ? t('projectDetail.folderApprovalChainTags.newUploadsGet', {
        name: newUploadsApprovalChain.name,
      })
    : undefined;

  const loadedTotal = data?.total ?? files.length;
  const isPartial = chains.length > 0 && loadedTotal > files.length;

  return (
    <div
      role="group"
      className="flex flex-wrap items-center gap-2"
      data-testid="folder-approval-chain-tags"
      aria-label={t('projectDetail.folderApprovalChainTags.approvalChainSummary')}
      title={
        isPartial
          ? t('projectDetail.folderApprovalChainTags.countsCoverLoadedAssets', {
              loaded: files.length,
              total: loadedTotal,
            })
          : undefined
      }
    >
      {newUploadsApprovalChain && (
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            {/* The Badge holds its own trigger for a clipped label, so this one sits on a wrapper. */}
            <TooltipTrigger asChild>
              <span className="inline-flex rounded-full">
                <Badge
                  variant="secondary"
                  size="sm"
                  roundness="full"
                  className="gap-1.5"
                  data-testid="folder-new-uploads-approval-chain"
                >
                  <ListChecksIcon className="size-3.5 shrink-0" aria-hidden />
                  <span aria-hidden>{newUploadsApprovalChain.name}</span>
                  <span className="sr-only">{newUploadsLabel}</span>
                </Badge>
              </span>
            </TooltipTrigger>
            <TooltipContent aria-hidden>{newUploadsLabel}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      {tags.map((tag) => (
        <Badge
          key={tag.key}
          variant={tag.approved === tag.total ? 'positive' : 'warning'}
          size="sm"
          roundness="full"
          className="gap-1.5"
        >
          <ListChecksIcon className="size-3.5 shrink-0" />
          {tag.name
            ? t('projectDetail.folderApprovalChainTags.tagProgress', {
                name: tag.name,
                approved: tag.approved,
                total: tag.total,
              })
            : t('projectDetail.folderApprovalChainTags.progress', {
                approved: tag.approved,
                total: tag.total,
              })}
        </Badge>
      ))}
    </div>
  );
}
